/**
 * Booking page cover/header presets.
 * ----------------------------------
 * Shared between Settings.jsx (the picker) and PublicBooking.jsx (the
 * renderer). Each preset is pure CSS — gradients + soft glow blobs —
 * so we never need to host or pre-load any imagery.
 *
 * Design rules:
 *   • Logo + text are ALWAYS rendered on top as separate elements; the
 *     preset only paints the background of the <header>. The logo is
 *     never used as a stretched cover image.
 *   • Each preset declares an `isDark` flag so the eyebrow and body copy
 *     can flip to the right contrast tier.
 *   • Default (null / unknown id) falls back to the legacy emerald
 *     layout so existing businesses are not visually disrupted.
 *
 * Storage:
 *   • The selected preset id is stored on `Business.booking_cover_preset`
 *     as a string (one of `BOOKING_COVER_PRESET_IDS`) or null.
 *   • Server validates against this same id list — unknown values are
 *     rejected at PUT /business.
 */

/** Preset registry. Order = order shown in the Settings picker. */
export const BOOKING_COVER_PRESETS = [
  {
    id: "salon-rose",
    label: "Salong / frisør",
    description: "Varm rose + gylden glød. Passer hårsalonger og barbershops.",
    isDark: false,
    headerStyle: {
      backgroundImage:
        "linear-gradient(135deg, #fff5f1 0%, #fde7df 35%, #f6c9b0 100%)",
    },
    glowStyle: {
      background:
        "radial-gradient(closest-side, rgba(212, 132, 84, 0.35), transparent 70%)",
    },
    borderClass: "border-b border-[#e9b894]/40",
  },
  {
    id: "beauty-blush",
    label: "Skjønnhet / behandling",
    description: "Champagnerosa med myk glød. Passer skjønnhetssalonger.",
    isDark: false,
    headerStyle: {
      backgroundImage:
        "linear-gradient(140deg, #fdf3f7 0%, #f9dde7 45%, #efbcd1 100%)",
    },
    glowStyle: {
      background:
        "radial-gradient(closest-side, rgba(217, 130, 165, 0.32), transparent 70%)",
    },
    borderClass: "border-b border-[#e9b3cb]/40",
  },
  {
    id: "wellness-sage",
    label: "Massasje / velvære",
    description: "Salviegrønn på krem. Passer massasje, yoga, velvære.",
    isDark: false,
    headerStyle: {
      backgroundImage:
        "linear-gradient(135deg, #f4f8f1 0%, #e2ecdc 45%, #b9d2af 100%)",
    },
    glowStyle: {
      background:
        "radial-gradient(closest-side, rgba(120, 158, 102, 0.32), transparent 70%)",
    },
    borderClass: "border-b border-[#bccdaf]/50",
  },
  {
    id: "consultant-slate",
    label: "Konsulent / service",
    description: "Kjølig skifer + indigo. Passer rådgivere og fagservice.",
    isDark: true,
    headerStyle: {
      backgroundImage:
        "linear-gradient(135deg, #11203a 0%, #1c2f51 45%, #2c4275 100%)",
    },
    glowStyle: {
      background:
        "radial-gradient(closest-side, rgba(99, 142, 219, 0.38), transparent 70%)",
    },
    borderClass: "border-b border-white/10",
  },
  {
    id: "minimal-dark",
    label: "Minimal mørk",
    description: "Matt sort med en hårfin grønn aksent. Tidløst.",
    isDark: true,
    headerStyle: {
      backgroundImage:
        "linear-gradient(180deg, #0c0f0d 0%, #131815 60%, #0c0f0d 100%)",
    },
    glowStyle: {
      background:
        "radial-gradient(closest-side, rgba(16, 185, 129, 0.20), transparent 70%)",
    },
    borderClass: "border-b border-white/[0.06]",
  },
  {
    id: "minimal-light",
    label: "Minimal lys",
    description: "Ren hvit bakgrunn med en lett emerald-streke.",
    isDark: false,
    headerStyle: {
      backgroundImage:
        "linear-gradient(180deg, #ffffff 0%, #f7f9f8 100%)",
    },
    glowStyle: {
      background:
        "radial-gradient(closest-side, rgba(16, 185, 129, 0.12), transparent 70%)",
    },
    borderClass: "border-b border-emerald-500/15",
  },
];

export const BOOKING_COVER_PRESET_IDS = BOOKING_COVER_PRESETS.map((p) => p.id);

/**
 * Look up a preset by id. Returns null for unknown / missing ids so the
 * caller can render the legacy fallback header.
 */
export function getBookingCoverPreset(id) {
  if (!id) return null;
  return BOOKING_COVER_PRESETS.find((p) => p.id === id) || null;
}
